import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Button from './Button';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);

      // Update active section based on scroll position
      const sections = ['hero', 'about', 'chairman', 'capabilities', 'products', 'process', 'quality', 'sectors', 'testimonials', 'contact'];
      let currentActiveSection = 'hero';

      // Check which section is currently in view
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollY;

          // Consider a section active if we've scrolled past its start point (with offset)
          // Use different offset for hero vs other sections
          const offset = sections[i] === 'hero' ? 50 : 150;
          if (scrollY >= elementTop - offset) {
            currentActiveSection = sections[i];
            break;
          }
        }
      }

      setActiveSection(currentActiveSection);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navItems = [
    {
      name: 'About',
      href: '#about',
      id: 'about',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      name: 'Chairman',
      href: '#chairman',
      id: 'chairman',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    { 
      name: 'Capabilities', 
      href: '#capabilities', 
      id: 'capabilities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    { 
      name: 'Products', 
      href: '#products', 
      id: 'products',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      )
    },
    { 
      name: 'Process', 
      href: '#process', 
      id: 'process',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )
    },
    { 
      name: 'Quality', 
      href: '#quality', 
      id: 'quality',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    { 
      name: 'Sectors', 
      href: '#sectors', 
      id: 'sectors',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    { 
      name: 'Testimonials', 
      href: '#testimonials', 
      id: 'testimonials',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      )
    },
    { 
      name: 'Contact', 
      href: '#contact', 
      id: 'contact',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )
    }
  ];

  const handleNavClick = (href) => {
    // Close mobile menu first
    setIsMobileMenuOpen(false);

    // Single, clean scroll method to prevent bouncing
    const targetId = href.replace('#', '');

    // Function to wait for element and then scroll
    const waitForElementAndScroll = (attempts = 0, maxAttempts = 50) => {
      const element = document.getElementById(targetId);

      if (element) {
        // Prevent any default hash behavior that might cause bouncing
        window.history.replaceState(null, null, ' ');

        // Calculate position with proper offset for grouped sections
        let elementPosition;
        if (targetId === 'hero') {
          elementPosition = 0; // Hero is at the top
        } else {
          // For sections within grouped-sections, get absolute position
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          elementPosition = rect.top + scrollTop - 80;
        }

        // Mobile-optimized scroll method
        if (/Mobi|Android/i.test(navigator.userAgent)) {
          // For mobile devices, try multiple scroll methods

          // Method 1: Immediate scroll
          window.scrollTo(0, elementPosition);

          // Method 2: Force scroll using different techniques
          setTimeout(() => {
            // Try different scroll methods for better mobile compatibility
            if (window.scrollTo) {
              window.scrollTo({
                top: elementPosition,
                left: 0,
                behavior: 'smooth'
              });
            }

            // Alternative method using documentElement
            if (document.documentElement.scrollTo) {
              document.documentElement.scrollTo({
                top: elementPosition,
                left: 0,
                behavior: 'smooth'
              });
            }

            // Alternative method using body
            if (document.body.scrollTo) {
              document.body.scrollTo({
                top: elementPosition,
                left: 0,
                behavior: 'smooth'
              });
            }
          }, 50);

          // Final fallback - direct style manipulation
          setTimeout(() => {
            document.documentElement.scrollTop = elementPosition;
            document.body.scrollTop = elementPosition;
          }, 150);

        } else {
          // Desktop smooth scroll
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      } else if (attempts < maxAttempts) {
        // Element not found, try again after a short delay
        // Element not found, retry
        setTimeout(() => {
          waitForElementAndScroll(attempts + 1, maxAttempts);
        }, 100);
      } else {
        // Fallback: Try scrolling by estimated position based on section order
        const sectionOrder = ['hero', 'about', 'chairman', 'capabilities', 'products', 'process', 'quality', 'sectors', 'testimonials', 'contact'];
        const sectionIndex = sectionOrder.indexOf(targetId);

        if (sectionIndex > 0) {
          // Estimate position (assuming each section is roughly 600px)
          const estimatedPosition = sectionIndex * 600;

          // Mobile-optimized fallback scroll
          if (/Mobi|Android/i.test(navigator.userAgent)) {
            window.scrollTo(0, estimatedPosition);
            setTimeout(() => {
              window.scrollTo({
                top: estimatedPosition,
                behavior: 'smooth'
              });
            }, 100);
          } else {
            window.scrollTo({
              top: estimatedPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    };

    // Start the waiting process
    waitForElementAndScroll();
  };

  const shouldBeTransparent = isDesktop && !isScrolled;
  
  const navbarClasses = `
    fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out
    ${!shouldBeTransparent
      ? `${isDarkMode 
          ? 'bg-white/10 border-neutral-200/20' 
          : 'bg-black/70 border-neutral-800/50'
        } backdrop-blur-xl border-b shadow-lg shadow-black/5`
      : 'bg-transparent border-transparent'
    }
  `;

  return (
    <>
      <motion.nav
        className={navbarClasses}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-500 ${
            isScrolled ? 'h-16' : 'h-20'
          }`}>
            
            {/* Logo */}
            <motion.div 
              className="flex-shrink-0 z-10"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <a 
                href="#hero" 
                className="transition-all duration-300 hover:scale-105"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('#hero');
                }}
              >
                <img 
                  src="/Julfikarlogo.png" 
                  alt="Julfikar Steel" 
                  className={`h-12 w-auto transition-all duration-300 ${
                    shouldBeTransparent 
                      ? 'drop-shadow-lg' 
                      : ''
                  }`}
                />
              </a>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
              <div className={`flex items-center space-x-1 p-1 rounded-full transition-all duration-300 ${
                shouldBeTransparent
                  ? 'bg-transparent' 
                  : `${isDarkMode ? 'bg-neutral-100/20' : 'bg-neutral-900/60'} backdrop-blur-sm`
              }`}>
                {navItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <motion.a
                      key={item.name}
                      href={item.href}
                      className={`
                        relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
                        ${isActive 
                          ? 'text-black' 
                          : shouldBeTransparent
                            ? 'text-white drop-shadow-md hover:text-primary-300'
                            : `${isDarkMode ? 'text-white hover:text-primary-300' : 'text-white hover:text-primary-300'}`
                        }
                      `}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.href);
                      }}
                      whileHover={{ y: -1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {isActive && (
                        <motion.div
                          className="absolute inset-0 bg-primary-400 rounded-full"
                          layoutId="navbar-active"
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      )}
                      <span className="relative z-10">{item.name}</span>
                    </motion.a>
                  );
                })}
              </div>
            </div>

            {/* Theme Toggle & CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle className="scale-90" />
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  variant="primary" 
                  size="sm"
                  className="font-semibold"
                  onClick={() => handleNavClick('#contact')}
                >
                  Get Quote
                </Button>
              </motion.div>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center space-x-3">
              <ThemeToggle className="scale-75" />
              <motion.button
                className={`
                  relative w-10 h-10 rounded-lg flex items-center justify-center
                  ${shouldBeTransparent 
                    ? 'text-white drop-shadow-md hover:text-primary-300' 
                    : `${colors.text.primary} hover:text-primary-400`
                  } 
                  ${!shouldBeTransparent
                    ? `${isDarkMode ? 'bg-neutral-100/20' : 'bg-neutral-900/60'} backdrop-blur-sm` 
                    : ''
                  }
                  transition-all duration-300
                `}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                whileTap={{ scale: 0.95 }}
              >
                <span className="sr-only">Open menu</span>
                <div className="w-5 h-5 flex flex-col justify-center space-y-1">
                  <motion.span
                    className="w-full h-0.5 bg-current rounded-full"
                    animate={{
                      rotate: isMobileMenuOpen ? 45 : 0,
                      y: isMobileMenuOpen ? 4 : 0,
                      opacity: isMobileMenuOpen ? 1 : 1
                    }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  />
                  <motion.span
                    className="w-full h-0.5 bg-current rounded-full"
                    animate={{
                      opacity: isMobileMenuOpen ? 0 : 1,
                      scale: isMobileMenuOpen ? 0.8 : 1
                    }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  />
                  <motion.span
                    className="w-full h-0.5 bg-current rounded-full"
                    animate={{
                      rotate: isMobileMenuOpen ? -45 : 0,
                      y: isMobileMenuOpen ? -4 : 0,
                      opacity: isMobileMenuOpen ? 1 : 1
                    }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  />
                </div>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className={`
                lg:hidden absolute top-full left-0 right-0 overflow-hidden
                ${isDarkMode
                  ? 'bg-black/95 border-white/30'
                  : 'bg-white/95 border-black/30'
                }
                backdrop-blur-3xl border-t shadow-2xl glass-mobile-menu
              `}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <div className="px-4 py-6">
                {/* Grid Layout for Mobile Menu Items */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {navItems.map((item, index) => {
                    const isActive = activeSection === item.id;
                    return (
                      <motion.a
                        key={item.name}
                        href={item.href}
                        className={`
                          flex flex-col items-center justify-center p-4 rounded-2xl text-center transition-all duration-300 min-h-[90px] border
                          ${isActive 
                            ? 'bg-primary-400 text-black border-primary-400 shadow-lg'
                            : `${isDarkMode
                                ? 'bg-white/30 border-white/40 text-white hover:bg-white/40 hover:border-primary-400/70'
                                : 'bg-black/30 border-black/40 text-black hover:bg-black/40 hover:border-primary-400/70'
                              } backdrop-blur-lg hover:shadow-xl glass-menu-item`
                          }
                        `}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleNavClick(item.href);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleNavClick(item.href);
                        }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        {/* Icon */}
                        <div className={`mb-2 ${isActive ? 'text-black' : 'text-primary-400'}`}>
                          {item.icon}
                        </div>
                        {/* Text */}
                        <span className="text-sm font-medium leading-tight">
                          {item.name}
                        </span>
                      </motion.a>
                    );
                  })}
                </div>
                
                {/* Mobile CTA Button */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: navItems.length * 0.05, duration: 0.3 }}
                  className="pt-4 border-t border-neutral-200/20"
                >
                  <motion.button
                    className={`
                      w-full flex items-center justify-center p-4 rounded-2xl font-semibold text-base transition-all duration-300
                      bg-primary-400/90 text-black hover:bg-primary-500/90 shadow-lg hover:shadow-xl border border-primary-400/50
                      backdrop-blur-lg glass-cta-button
                    `}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavClick('#contact');
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavClick('#contact');
                    }}
                    whileTap={{ scale: 0.98 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Get Your Quote
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
      
      {/* Spacer to prevent content from hiding behind fixed navbar */}
      <div className={`transition-all duration-500 ${isScrolled ? 'h-16' : 'h-20'}`}></div>
    </>
  );
};

export default Navbar;